import uniq from 'lodash/uniq';
import React, { useCallback, memo } from 'react';
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
} from 'react-beautiful-dnd';
import TaskItemTextArea from '../../../atomic/molecules/Notebook/TaskItem/TaskItemTextArea';
import BoardTaskItem from '../../../atomic/organism/Notebook/TaskItem/BoardTaskItem';
import useMobileApp from '../../../hooks/useMobileApp';
import {
  ADD_NEW_TASK_FOOTER,
  EMPTY_BOARD_SECTIONS_TEXT,
  NOTEBOOK_TASK_TITLE_PLACEHOLDER,
} from '../../../languages/en/notebook';
import useNotebookStore from '../../../stores/notebookStore';
import { TaskLoader } from './Loader';
import {
  DropZone,
  SectionContainer,
  TaskContainer,
  SectionEmptyStateContainer,
  ScrollContainer,
  AddNewTaskButton,
  CreateTaskTextAreaWrapper,
  SectionalContentWrapper,
  AddNewTaskButtonLabel,
  StyledInfiniteScroll,
  Wrapper,
} from './styles';
import { SectionProps, TaskCreationPosition } from './types';
import useNotebookBoardTasksController from './useNotebookBoardTasksController';

const Section = (props: SectionProps) => {
  const {
    section,
    taskCreationPosition,
    isCreateTaskMode,
    isLoading,
    handleTaskCreationTextBlur,
    handleTaskCreationEnterClick,
    handleTaskCreationButtonClick,
    markTasksAsRead,
    onTaskDataChange,
  } = props;
  const notes = useNotebookStore(
    useCallback(
      // TODO: Sumedha - debug about duplicates getting added
      (store) => uniq(store.boardNotes[section.sectionId]?.tasks || []),
      [section],
    ),
  );

  const { isMobileApp } = useMobileApp();

  const {
    assigneesList,
    hasMoreAssigneesToFetch,
    isFetchingMoreAssignees,
    searchValue,
    currentUserId,
    currentUserTimezone,
    fetchMoreAssignees,
    handleAssigneeChange,
    handleSearchOnAssigneeList,
    setTaskIdOfFocusedTask,
    editTaskContent,
    updateTask,
    isTasksLoading,
    hasMoreTasksToFetch,
    isFetchingMoreTasks,
    fetchMoreTasks,
    onTaskClick,
    dropdownOptions,
    archiveTask,
    transactionIds,
    erroredTaskIDs,
    handleCreatorLinkClick,
    updateReadStatusOfTask,
  } = useNotebookBoardTasksController(
    section.sectionId,
    undefined,
    undefined,
    markTasksAsRead,
  );

  const renderSectionalContent = useCallback(
    (isDraggingOver: boolean, placeholder?: React.ReactElement | null) => {
      if (
        notes.length < 1 &&
        !isDraggingOver &&
        !isCreateTaskMode &&
        section?.isCompleted
      ) {
        return (
          <SectionEmptyStateContainer justifyContent="center">
            {EMPTY_BOARD_SECTIONS_TEXT}
          </SectionEmptyStateContainer>
        );
      }
      return (
        <StyledInfiniteScroll
          dataLength={notes?.length || 0}
          hasMore={hasMoreTasksToFetch || false}
          loader={<TaskLoader />}
          next={fetchMoreTasks}
          scrollableTarget={section.sectionId}
          hasTasks={(notes?.length || 0) > 0}
          hasAddButton={!(section?.isCompleted || section?.isUnassigned)}
          isMobileApp={isMobileApp}
          className={`section-${section.sectionId}`}
        >
          {(isFetchingMoreTasks || isTasksLoading) && <TaskLoader />}
          {isCreateTaskMode &&
            taskCreationPosition === TaskCreationPosition.top && (
              <CreateTaskTextAreaWrapper position={taskCreationPosition}>
                <TaskItemTextArea
                  onBlur={handleTaskCreationTextBlur}
                  handleEnterClick={handleTaskCreationEnterClick}
                  hasHoverEffect={false}
                  isCreateItem
                  placeholder={NOTEBOOK_TASK_TITLE_PLACEHOLDER}
                />
              </CreateTaskTextAreaWrapper>
            )}
          {(notes || []).map((taskId: string, index: number) => (
            <Draggable
              key={taskId}
              draggableId={taskId}
              isDragDisabled={transactionIds?.includes(taskId)}
              index={index}
            >
              {(
                dragProvided: DraggableProvided,
                dragSnapshot: DraggableStateSnapshot,
              ) => (
                <TaskContainer
                  ref={dragProvided.innerRef}
                  {...dragProvided.draggableProps}
                  {...dragProvided.dragHandleProps}
                  data-is-dragging={dragSnapshot.isDragging}
                  data-testid={taskId}
                  data-index={index}
                  hasAddButton={
                    !(section?.isCompleted || section?.isUnassigned)
                  }
                  isMobileApp={isMobileApp}
                >
                  <BoardTaskItem
                    isLoading={false}
                    index={0}
                    taskId={taskId}
                    dropdownOptions={dropdownOptions}
                    onEditorUpdate={editTaskContent}
                    updateTask={updateTask}
                    onTaskClick={onTaskClick}
                    setTaskIdOfFocusedTask={setTaskIdOfFocusedTask}
                    fetchMoreAssignees={fetchMoreAssignees}
                    assigneesList={assigneesList}
                    hasMoreAssigneesToFetch={hasMoreAssigneesToFetch}
                    isFetchingMoreAssignees={isFetchingMoreAssignees}
                    searchValue={searchValue}
                    handleSearchOnAssigneeList={handleSearchOnAssigneeList}
                    handleAssigneeChange={handleAssigneeChange}
                    currentUserId={currentUserId || ''}
                    markTasksAsRead={updateReadStatusOfTask}
                    handleCreatorLinkClick={handleCreatorLinkClick}
                    currentUserTimezone={currentUserTimezone}
                    shouldShowModalForCurrentUser
                    archiveTask={archiveTask}
                    hasError={erroredTaskIDs?.includes(taskId)}
                    onTaskDataChange={onTaskDataChange}
                  />
                </TaskContainer>
              )}
            </Draggable>
          ))}
          {placeholder}
          {isCreateTaskMode &&
            taskCreationPosition === TaskCreationPosition.bottom && (
              <CreateTaskTextAreaWrapper position={TaskCreationPosition.bottom}>
                <TaskItemTextArea
                  onBlur={handleTaskCreationTextBlur}
                  handleEnterClick={handleTaskCreationEnterClick}
                  hasHoverEffect={false}
                  isCreateItem
                  placeholder={NOTEBOOK_TASK_TITLE_PLACEHOLDER}
                />
              </CreateTaskTextAreaWrapper>
            )}
        </StyledInfiniteScroll>
      );
    },
    [
      notes,
      isCreateTaskMode,
      section?.isCompleted,
      section.sectionId,
      section?.isUnassigned,
      hasMoreTasksToFetch,
      fetchMoreTasks,
      isFetchingMoreTasks,
      isTasksLoading,
      taskCreationPosition,
      handleTaskCreationTextBlur,
      handleTaskCreationEnterClick,
      transactionIds,
      isMobileApp,
      dropdownOptions,
      editTaskContent,
      updateTask,
      onTaskClick,
      setTaskIdOfFocusedTask,
      fetchMoreAssignees,
      assigneesList,
      hasMoreAssigneesToFetch,
      isFetchingMoreAssignees,
      searchValue,
      handleSearchOnAssigneeList,
      handleAssigneeChange,
      currentUserId,
      updateReadStatusOfTask,
      handleCreatorLinkClick,
      currentUserTimezone,
      archiveTask,
      erroredTaskIDs,
      onTaskDataChange,
    ],
  );

  return (
    <Droppable
      droppableId={section?.sectionId}
      isDropDisabled={isLoading}
      type="TASK"
    >
      {(
        dropProvided: DroppableProvided,
        dropSnapshot: DroppableStateSnapshot,
      ) => (
        <>
          <Wrapper>
            <ScrollContainer
              id={section.sectionId}
              data-testid="tasks-board-scroll"
              hasRoundedBorder={
                section?.isCompleted ||
                section?.isUnassigned ||
                dropSnapshot.isDraggingOver
              }
              isDraggingOver={dropSnapshot.isDraggingOver || notes?.length < 1}
              {...dropProvided.droppableProps}
            >
              <SectionContainer
                hasAddButton={!(section?.isCompleted || section?.isUnassigned)}
                isMobileApp={isMobileApp}
              >
                <DropZone ref={dropProvided.innerRef}>
                  <SectionalContentWrapper>
                    {renderSectionalContent(
                      dropSnapshot.isDraggingOver,
                      dropProvided.placeholder,
                    )}
                  </SectionalContentWrapper>
                </DropZone>
              </SectionContainer>
            </ScrollContainer>
          </Wrapper>
          {!dropSnapshot.isDraggingOver && (
            <AddNewTaskButton sticky={notes?.length > 0}>
              {!(section?.isCompleted || section?.isUnassigned) && (
                <AddNewTaskButtonLabel
                  role="button"
                  justifyContent="center"
                  onClick={() =>
                    handleTaskCreationButtonClick(TaskCreationPosition.bottom)
                  }
                >
                  {ADD_NEW_TASK_FOOTER}
                </AddNewTaskButtonLabel>
              )}
            </AddNewTaskButton>
          )}
        </>
      )}
    </Droppable>
  );
};

export default memo(Section);
